import React from 'react';

import { StyleSheet, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CustomDisclaimer } from 'components/offer-disclaimer/offer-disclaimer';
import { Typography } from 'components/ui';
import { parseIncentiveData } from 'pages/loyalty/loyalty-incentives-components/incentive-card/parse-incentive-data';
import {
  ImageWrapper,
  RewardDescription,
  RewardDetailsContainer,
  RewardName,
  StyledDisclaimerContainer,
  StyledRewardImage,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details.styled';
import { t } from 'state/intl/translations';
import { LoyaltyOffer } from 'state/loyalty/types';
import { spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

const IMAGE_MIN_HEIGHT = '200px';

type OfferNoStorePreviewProps = {
  offer: LoyaltyOffer;
  onSelectRestaurant: () => void;
  onDismiss?: () => void;
};

/**
 * No-store offer deeplink preview: the requested offer's artwork, name,
 * description and terms, then the restaurant CTA. Mirrors the menu catalog
 * preview so a storeless deeplink shows the incentive the guest asked for
 * instead of a location placeholder.
 */
export function OfferNoStorePreview({
  offer,
  onSelectRestaurant,
  onDismiss,
}: OfferNoStorePreviewProps) {
  const { name, image, imageDescription, description, moreInfo } = parseIncentiveData(offer);

  return (
    <CardModal
      alwaysShowHeader
      headerText={t('offer')}
      onDismiss={onDismiss}
      scrollView
      standardPadding
      footer={
        <View style={styles.footer}>
          <ActionButton
            testID="offer-no-store-preview-select-restaurant"
            fullWidth
            variant={ActionButtonVariants.PRIMARY}
            onPress={onSelectRestaurant}
            leftIcon={
              <Icon name="mi:location-on" width="24px" {...hiddenAccessibilityPlatformProps} />
            }
          >
            {t('selectRestaurantToContinue')}
          </ActionButton>
        </View>
      }
    >
      <View testID="offer-no-store-preview">
        {image ? (
          <ImageWrapper>
            <StyledRewardImage
              alt={imageDescription}
              image={image}
              isBlurhashEnabled={false}
              minHeight={IMAGE_MIN_HEIGHT}
              priority="high"
            />
          </ImageWrapper>
        ) : null}

        <RewardDetailsContainer>
          {name ? <RewardName>{name}</RewardName> : null}
          {description ? <RewardDescription content={description} /> : null}
        </RewardDetailsContainer>

        {moreInfo ? (
          <StyledDisclaimerContainer style={styles.terms}>
            <Typography variant="headerThree">{t('offerTerms')}</Typography>
            <CustomDisclaimer content={moreInfo} textProps={{ textAlign: 'left' }} />
          </StyledDisclaimerContainer>
        ) : null}
      </View>
    </CardModal>
  );
}

const styles = StyleSheet.create({
  footer: {
    paddingHorizontal: spacing.$4,
    width: '100%',
  },
  terms: {
    flex: 0,
  },
});
